import React from 'react';
import {
  Button,
  Confirmation,
  Icons,
  Input,
  Loader,
  Menu,
  Popover,
  Tabs,
  Toggle,
  Tooltip,
  useToasts,
} from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { omit } from 'lodash';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import CreatedBy from 'routes/components/CreatedBy';
import ElementInView from 'shared/components/ElementInView';
import EmptyState from 'shared/components/EmptyState';
import Icon from 'shared/components/Icon';
import Reorder from 'shared/components/Reorder';
import { limits } from 'shared/constants';
import { Center } from 'shared/styles';
import {
  AccountInfoType,
  ConfirmationType,
  WorkflowType,
  WorkflowTypeOptions,
} from 'shared/types';
import { getAccountTimeFormat } from 'shared/utils';
import { useAccessControl } from 'utils/access-control';
import { WorkflowFilterOptionTypes } from '../types';
import {
  useDeleteWorkflow,
  useReorderWorkflows,
  useSaveWorkflow,
  useWorkflows,
} from '../hooks';
import {
  getStatusFilter,
  getWorkflowFilterCount,
  getWorkflowTypeFilter,
} from '../utils';
import {
  WorkflowListCell,
  WorkflowListContainer,
  WorkflowListHeader,
  WorkflowListTable,
} from '../styles';
import {
  NUMBER_OF_ALLOWED_WORKFLOWS,
  WorkFlowActionOptions,
  WorkFlowTypeOptions,
  WorkflowFilterOptions,
} from '../constants';

type WorkflowsProps = {
  accountInfo: AccountInfoType;
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
};

const Workflows = (props: WorkflowsProps) => {
  const { accountInfo, setConfirmationData } = props;
  const { t } = useTranslation();
  const { access = [] } = useAccessControl();
  const [, setToast] = useToasts();
  const { state: locationState } = useLocation();
  const { data: workflowsData, isLoading: isLoadingWorkflows } = useWorkflows();
  const { mutate: deleteWorkflow, isLoading: isDeletingWorkflow } =
    useDeleteWorkflow({
      setToast,
      callback: (isSuccess) => {
        if (isSuccess) {
          setConfirmationData({});
        }
      },
    });
  const { mutate: reorderWorkflows, isLoading: isReorderPending } =
    useReorderWorkflows({
      setToast,
      callback: (isSuccess) => {
        if (isSuccess) {
          setWorkflowsToReorder(null);
        }
        setFilters({
          ...filters,
          status: 'ALL',
          reorder: false,
        });
      },
    });
  const {
    mutate: saveWorkflow,
    isLoading: isSavingWorkflow,
    variables: saveWorkflowVariables,
  } = useSaveWorkflow({
    setToast,
    callback: (isSuccess) => {
      if (isSuccess) {
        setConfirmationData({});
        setWorkflowToDuplicate(null);
      }
    },
  });

  const [workflowsToReorder, setWorkflowsToReorder] = React.useState<
    WorkflowType[] | null
  >(null);
  const [workflowToDuplicate, setWorkflowToDuplicate] =
    React.useState<WorkflowType | null>(null);
  const [filterAnchorEl, setFilterAnchorEl] = React.useState<any>(null);
  const [anchorEl, setAnchorEl] = React.useState<any>(null);
  const [filters, setFilters] = React.useState<{
    status: WorkflowFilterOptionTypes;
    type: WorkflowTypeOptions;
    reorder: boolean;
  }>({
    status: 'ALL',
    type: (locationState as any)?.type ?? 'AUTOMATIC',
    reorder: false,
  });

  const form = useForm<WorkflowType>({
    resolver: yupResolver(
      Yup.object().shape({
        name: Yup.string()
          .trim()
          .required(`${t('forms.err_msg.name_required')}`)
          .max(
            90,
            `${t('forms.err_msg.max_char', {
              len: '50',
            })}`,
          )
          .min(
            3,
            `${t('forms.err_msg.min_char', {
              len: '3',
            })}`,
          ),
      }),
    ),
  });

  const WorkflowLimit = React.useMemo(
    () =>
      (Number(accountInfo?.resource_limit_configuration?.workflows) ||
        limits.workflows) ??
      NUMBER_OF_ALLOWED_WORKFLOWS,
    [accountInfo],
  );
  const AutomaticWorkflowsCount = React.useMemo(
    () =>
      (workflowsData?.workflows || []).filter((wf) => wf.type === 'AUTOMATIC')
        .length,
    [workflowsData],
  );
  const ManualWorkflowsCount = React.useMemo(
    () =>
      (workflowsData?.workflows || []).filter((wf) => wf.type === 'MANUAL')
        .length,
    [workflowsData],
  );
  const workflowTypeOptions = React.useMemo(() => {
    return Object.keys(WorkFlowTypeOptions).map((key) => ({
      id: key as WorkflowTypeOptions,
      name: `${t(WorkFlowTypeOptions[key])} ${
        key === 'AUTOMATIC'
          ? `${AutomaticWorkflowsCount ? `(${AutomaticWorkflowsCount})` : ''}`
          : `${ManualWorkflowsCount ? `(${ManualWorkflowsCount})` : ''}`
      }`.trim(),
    }));
  }, [t, AutomaticWorkflowsCount, ManualWorkflowsCount]);
  const workflowFilterOptions = React.useMemo(() => {
    return Object.keys(WorkflowFilterOptions).map((key) => ({
      id: key as WorkflowFilterOptionTypes,
      name: `${t(WorkflowFilterOptions[key])} ${
        workflowsData?.workflows
          ? `(${getWorkflowFilterCount({
              filterType: key as WorkflowFilterOptionTypes,
              workflowsData,
              workflowType: filters.type,
            })})`
          : ''
      }`,
      disabled:
        workflowsData?.workflows &&
        getWorkflowFilterCount({
          filterType: key as WorkflowFilterOptionTypes,
          workflowsData,
          workflowType: filters.type,
        }) === 0,
    }));
  }, [t, workflowsData, filters]);
  const workflowsToDisplay = React.useMemo(() => {
    const filteredWorkflows = (workflowsData?.workflows || [])
      .filter(
        (wf) =>
          getStatusFilter({ status: filters.status, workflow: wf }) &&
          getWorkflowTypeFilter({ type: filters.type, workflow: wf }),
      )
      .sort((a, b) => {
        if (a.order && b.order) {
          return a.order - b.order;
        }
        return 0;
      });
    filters.reorder
      ? setWorkflowsToReorder(filteredWorkflows)
      : setWorkflowsToReorder(null);
    return filteredWorkflows;
  }, [workflowsData, filters]);

  return (
    <WorkflowListContainer>
      {workflowsData && workflowsToDisplay ? (
        <>
          <div className="wf-l-top-container">
            <div className="wf-t-c-top-pane">
              <div className="wf-t-c-left-pane">
                <div className="wf-l-heading">{`${t('sidebar.workflows')} (${
                  workflowsData?.workflows?.length ?? 0
                }/${WorkflowLimit}) `}</div>
                <div className="wf-l-description">{t('workflow.desc')}</div>
              </div>
              <div className="wf-t-c-right-pane">
                {access.includes('workflow:write') && (
                  <Link
                    to={{
                      pathname: '/workflow/create',
                      state: { type: filters.type },
                    }}
                  >
                    <Button
                      disabled={
                        isDeletingWorkflow ||
                        isLoadingWorkflows ||
                        (workflowsData?.workflows || [])?.length >=
                          WorkflowLimit
                      }
                    >
                      {`+ ${t('workflow.new')}`}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
            <div>
              <Tabs
                value={filters.type}
                onChange={(e, tab) =>
                  workflowsToReorder
                    ? false
                    : setFilters({ ...filters, type: tab.id })
                }
                options={workflowTypeOptions}
              />
            </div>
          </div>
          {workflowsData?.workflows && workflowsData?.workflows?.length > 0 && (
            <>
              <div className="wf-l-bottom-container">
                <div className="wf-l-auto-info">
                  <span className="wf-l-a-tp">
                    {filters.type === 'AUTOMATIC' && (
                      <>
                        <Icon glyph="info" width="16" height="16" />
                        &nbsp;
                        {t('workflow.note')}
                      </>
                    )}
                  </span>
                  {!workflowsToReorder && (
                    <div className="wf-l-a-filter">
                      {t('show')}:
                      <Button
                        type="secondary"
                        inline
                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                        aria-describedby="filter-button"
                      >
                        <span className="wf-f-s-count">
                          {
                            workflowFilterOptions.find(
                              (wfo) => wfo.id === filters.status,
                            )?.name
                          }
                        </span>
                        <Icon
                          glyph="arrow-down"
                          width={8}
                          height={8}
                          fill="#12344D"
                          className="down-arrow"
                        />
                      </Button>
                      <Popover
                        id="filter-button"
                        anchorEl={filterAnchorEl}
                        onClose={() => setFilterAnchorEl(null)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <Menu
                          options={workflowFilterOptions}
                          onSelect={(item) => {
                            setFilters({
                              ...filters,
                              status: item.id,
                            });
                          }}
                          closeMenuCallback={() => setFilterAnchorEl(null)}
                        />
                      </Popover>
                    </div>
                  )}
                  {access.includes('workflow:write') &&
                  filters.type === 'AUTOMATIC' ? (
                    workflowsToReorder ? (
                      <div
                        style={{
                          display: 'flex',
                          gap: '4px',
                        }}
                      >
                        <Button
                          type="secondary"
                          inline
                          onClick={() =>
                            setFilters({
                              ...filters,
                              status: 'ALL',
                              reorder: false,
                            })
                          }
                        >
                          {t('cancel')}
                        </Button>
                        <Button
                          inline
                          disabled={isReorderPending}
                          onClick={() => {
                            const payload: Record<string, number> = {};
                            workflowsToReorder?.forEach((wf, idx) => {
                              if (wf.workflow_id)
                                payload[wf.workflow_id] = idx + 1;
                            });
                            reorderWorkflows({
                              payload,
                              type: filters.type,
                            });
                          }}
                        >
                          {t('done')}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="secondary"
                        inline
                        disabled={isLoadingWorkflows}
                        onClick={() => {
                          setFilters({
                            ...filters,
                            status: 'ALL',
                            reorder: true,
                          });
                        }}
                      >
                        {t('reorder')}
                      </Button>
                    )
                  ) : (
                    <></>
                  )}
                </div>
                {workflowsToDisplay.length > 0 && (
                  <WorkflowListTable>
                    <WorkflowListHeader>
                      <div>
                        <WorkflowListCell className="wf-l-item-reorder header" />
                        <WorkflowListCell className="wf-l-item-title header">
                          {t('workflow.table_header.workflow_name')}
                        </WorkflowListCell>
                        <WorkflowListCell className="wf-l-item-actions wf-l-item-userinfo header">
                          {t('settings.saved_templates.fields.columnCrBy.name')}
                        </WorkflowListCell>
                        <WorkflowListCell className="wf-l-item-actions wf-l-item-userinfo header">
                          {t('settings.saved_templates.fields.columnCrOn.name')}
                        </WorkflowListCell>
                        <WorkflowListCell className="wf-l-item-toggle header">
                          {t(
                            'contacts.import_contact.default_fields.contact.status',
                          )}
                        </WorkflowListCell>
                        <WorkflowListCell className="wf-l-item-options header" />
                      </div>
                    </WorkflowListHeader>
                    <Reorder
                      items={workflowsToReorder ?? workflowsToDisplay}
                      showDragger={!!workflowsToReorder}
                      onReorder={(items: WorkflowType[]) => {
                        setWorkflowsToReorder(items);
                      }}
                      className="wf-l"
                      customRender={(workflow: WorkflowType, index: number) => {
                        return (
                          <>
                            {!workflowsToReorder && (
                              <WorkflowListCell className="wf-l-item-reorder" />
                            )}
                            <WorkflowListCell
                              className={`wf-l-item-title ${
                                workflowsToReorder ? 'disabled' : ''
                              }`}
                              title={workflow.name || ''}
                            >
                              <Link
                                onClick={
                                  workflowsToReorder
                                    ? (e) => e.preventDefault()
                                    : () => {}
                                }
                                to={`/workflow/edit/${workflow.workflow_id}`}
                              >
                                {filters.type === 'AUTOMATIC'
                                  ? `${index + 1}. `
                                  : ''}
                                {workflow.name}
                              </Link>
                              <WorkflowListCell
                                className="wf-l-item-actions"
                                padding="0"
                              >
                                {workflow.actions?.slice(0, 2).map((action) => (
                                  <div key={action.id} className="wf-l-i-a">
                                    {action.action_type
                                      ? t(
                                          WorkFlowActionOptions[
                                            action.action_type
                                          ],
                                        )
                                      : ''}
                                  </div>
                                ))}
                                {(workflow?.actions || []).length > 2 && (
                                  <Tooltip
                                    position="top"
                                    html={
                                      <div>
                                        {(workflow?.actions || [])
                                          .slice(2)
                                          .map((action) => (
                                            <div key={action.id}>
                                              {action.action_type
                                                ? t(
                                                    WorkFlowActionOptions[
                                                      action.action_type
                                                    ],
                                                  )
                                                : ''}
                                            </div>
                                          ))}
                                      </div>
                                    }
                                  >
                                    <span className="action">
                                      +{workflow.actions?.slice(2).length}
                                    </span>
                                  </Tooltip>
                                )}
                              </WorkflowListCell>
                            </WorkflowListCell>
                            <WorkflowListCell className="wf-l-item-actions wf-l-item-userinfo">
                              <ElementInView>
                                <div className="created-by">
                                  <CreatedBy
                                    userId={workflow.created_by_id || ''}
                                  />
                                  <div className="created-at">
                                    {workflow.created_at ? (
                                      <>
                                        {moment(new Date(workflow?.created_at))
                                          .tz(
                                            accountInfo?.timezone ||
                                              moment.tz.guess(),
                                          )
                                          .format(
                                            getAccountTimeFormat(accountInfo),
                                          )}
                                      </>
                                    ) : null}
                                  </div>
                                </div>
                              </ElementInView>
                            </WorkflowListCell>
                            <WorkflowListCell className="wf-l-item-actions wf-l-item-userinfo">
                              <ElementInView>
                                <div className="created-by">
                                  <CreatedBy
                                    userId={
                                      workflow.updated_by_id ||
                                      workflow.created_by_id ||
                                      ''
                                    }
                                  />
                                  <div className="created-at">
                                    {workflow.updated_at ? (
                                      <>
                                        {moment(new Date(workflow?.updated_at))
                                          .tz(
                                            accountInfo?.timezone ||
                                              moment.tz.guess(),
                                          )
                                          .format(
                                            getAccountTimeFormat(accountInfo),
                                          )}
                                      </>
                                    ) : null}
                                  </div>
                                </div>
                              </ElementInView>
                            </WorkflowListCell>
                            <WorkflowListCell className="wf-l-item-toggle">
                              <Toggle
                                {...{
                                  'aria-description': t(
                                    'contacts.menu.activate.text',
                                  ),
                                }}
                                on={!!workflow.active}
                                hasIcon={false}
                                handleChange={() => {
                                  saveWorkflow({
                                    ...workflow,
                                    active: !workflow.active,
                                  });
                                }}
                                disabled={
                                  (saveWorkflowVariables?.workflow_id ===
                                    workflow.workflow_id &&
                                    isSavingWorkflow) ||
                                  !!workflowsToReorder ||
                                  !access.includes('workflow:write')
                                }
                              />
                            </WorkflowListCell>
                            <WorkflowListCell className="wf-l-item-options">
                              {!workflowsToReorder &&
                                access.includes('workflow:write') && (
                                  <>
                                    <div
                                      className="delete-duplicate-workflow"
                                      onClick={(e) => {
                                        setAnchorEl(e.currentTarget);
                                        // setRowWorkflow(workflow);
                                      }}
                                      aria-describedby={`workflow-action-menu-${workflow.workflow_id}`}
                                    >
                                      <Icons
                                        glyph="more-vertical"
                                        size={12}
                                        viewBox="0 0 4 16"
                                      />
                                    </div>
                                    <Popover
                                      id={`workflow-action-menu-${workflow.workflow_id}`}
                                      anchorEl={anchorEl}
                                      onClose={() => setAnchorEl(null)}
                                      anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                      }}
                                      transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'center',
                                      }}
                                    >
                                      <Menu
                                        options={[
                                          {
                                            id: 'delete',
                                            name: `${t('delete')}`,
                                          },
                                          {
                                            id: 'duplicate',
                                            name: `${t('duplicate')}`,
                                            disabled:
                                              (workflowsData?.workflows || [])
                                                .length >=
                                              NUMBER_OF_ALLOWED_WORKFLOWS,
                                          },
                                        ]}
                                        onSelect={({ id }) => {
                                          switch (id) {
                                            case 'delete':
                                              setConfirmationData({
                                                id: new Date().toISOString(),
                                                title:
                                                  t(
                                                    'forms.confirmation.workflow',
                                                  ) || '',
                                                onConfirm: () => {
                                                  setConfirmationData(
                                                    (old) => ({
                                                      ...old,
                                                      confirmButtonDisabled:
                                                        true,
                                                    }),
                                                  );
                                                  deleteWorkflow(
                                                    workflow.workflow_id || '',
                                                  );
                                                },
                                                confirmButtonText: `${t(
                                                  'delete',
                                                )}`,
                                                className: 'error',
                                                fill: '#d7461f',
                                                infoText: `${t(
                                                  'contacts.menu.delete.infotext',
                                                )}`,
                                                description:
                                                  t(
                                                    'forms.confirmation.confirm_delete_workflow',
                                                  ) || '',
                                                glyph: 'info',
                                              });
                                              break;
                                            case 'duplicate':
                                              setWorkflowToDuplicate(workflow);
                                              form.reset({
                                                name: `${t('copy_of', {
                                                  label: workflow?.name,
                                                })}`,
                                              });
                                              break;
                                            default:
                                          }
                                        }}
                                        closeMenuCallback={() =>
                                          setAnchorEl(null)
                                        }
                                      />
                                    </Popover>
                                  </>
                                )}
                            </WorkflowListCell>
                          </>
                        );
                      }}
                    />
                  </WorkflowListTable>
                )}
              </div>
              <Confirmation
                width="540px"
                isOpen={!!workflowToDuplicate?.workflow_id}
                onDismiss={(type) => {
                  if (type === 'overlay') return;
                  setConfirmationData({});
                  setWorkflowToDuplicate(null);
                }}
                title={t('workflow.duplicate_workflow_field') || ''}
                confirmButtonDisabled={isSavingWorkflow}
                cancelButtonText={`${t('cancel')}`}
                confirmButtonText={`${t('save')}`}
                onConfirm={() => {
                  form.handleSubmit(
                    (data) => {
                      saveWorkflow({
                        ...omit(workflowToDuplicate, [
                          'id',
                          'workflow_id',
                          'created_at',
                          'updated_at',
                          'created_by_id',
                          'updated_by_id',
                          'order',
                        ]),
                        active: false,
                        actions: workflowToDuplicate?.actions?.map((action) =>
                          omit(action, ['id', 'workflow_id']),
                        ),
                        condition_groups:
                          workflowToDuplicate?.condition_groups?.map((cg) => ({
                            ...omit(cg, ['id', 'workflow_id']),
                            conditions: cg?.conditions?.map((c) => ({
                              ...omit(c, ['id', 'condition_group_id']),
                            })),
                          })),
                        name: data.name,
                      });
                    },
                    (err) => console.log('ERR', err),
                  )();
                }}
              >
                <Input
                  {...form.register('name')}
                  id="name"
                  label={`${t('workflow.table_header.workflow_name')}`}
                  disabled={isSavingWorkflow}
                  placeholder={`${t('forms.placeholders.workflow_name')}`}
                  isRequired
                  isErrored={!!form.formState.errors.name?.message}
                  errorText={form.formState.errors.name?.message || ''}
                />
              </Confirmation>
            </>
          )}
          {workflowsToDisplay.length === 0 && (
            <EmptyState
              title={`${t('empty_state.title.no_workflow_to_display')}`}
              message={
                access.includes('workflow:write')
                  ? `${t('empty_state.message.click_to_add_workflows')}`
                  : ''
              }
              imgSrc="empty-state-workflow.svg"
              minHeight="0px"
            >
              {access.includes('workflow:write') && (
                <Link
                  to={{
                    pathname: '/workflow/create',
                    state: { type: filters.type },
                  }}
                >
                  <Button
                    inline
                    disabled={
                      isDeletingWorkflow ||
                      isLoadingWorkflows ||
                      (workflowsData?.workflows || [])?.length >= WorkflowLimit
                    }
                  >
                    {`+ ${t('workflow.new')}`}
                  </Button>
                </Link>
              )}
            </EmptyState>
          )}
        </>
      ) : (
        <Center minHeight="calc(100% - 100px)">
          <Loader />
        </Center>
      )}
    </WorkflowListContainer>
  );
};

export default Workflows;
